/**
 * PopupReportChart - Bar chart for grouping report data by selected column
 *
 * Uses recharts (same library as VesselDashboard) to display aggregated report data.
 * Chart grouping options are configured per report type.
 */
import React, { useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from 'recharts';
import {
  ChartContainer,
  ChartTooltipContent,
  LoadingSpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ais-ui/ui-core';
import { ReportShipData } from '@ais-ui/websockets';
import { useLocalization } from '@ais-ui/locale-management';

/**
 * Chart grouping option configuration
 */
interface GroupingOption {
  value: string; // Column name to group by (case-insensitive match)
  label: string; // Display label
}

/**
 * Report types that produce visual-only output (heatmaps, tracks, images)
 * and do not have tabular ship data. The table section will be hidden for these.
 * Uses backend type values (from ReportBackendTypes enum values).
 */
export const REPORT_TYPES_WITHOUT_TABLE_DATA: string[] = [
  'DENSITY_TB', // Density heatmap - visual only
  'COVERAGE', // Coverage map - visual only
  'REPLAY_VIDEO', // Video output - no table
];

/**
 * Check if a report type has tabular data
 */
export const reportTypeHasTableData = (
  reportType: string | undefined,
): boolean => {
  if (!reportType) return true;
  return !REPORT_TYPES_WITHOUT_TABLE_DATA.includes(reportType);
};

/**
 * Chart configuration per report type
 * Each report type can specify which columns make sense to group by.
 * If a report type is not configured, DEFAULT_GROUPING_OPTIONS will be used
 * and filtered to only show options that exist in the data.
 */
const REPORT_TYPE_CHART_CONFIG: Record<string, GroupingOption[]> = {
  // Area report - vessels in an area
  Area: [
    { value: 'Country', label: 'Country' },
    { value: 'Ship type', label: 'Ship Type' },
    { value: 'Destination', label: 'Destination' },
  ],
  // Ships report
  Ships: [
    { value: 'Country', label: 'Country' },
    { value: 'Ship type', label: 'Ship Type' },
    { value: 'Destination', label: 'Destination' },
  ],
  // Port calls report
  'Port Calls': [
    { value: 'Country', label: 'Country' },
    { value: 'Ship type', label: 'Ship Type' },
    { value: 'Port', label: 'Port' },
    { value: 'From port', label: 'From Port' },
    { value: 'To port', label: 'To Port' },
  ],
  // Passageline report
  Passageline: [
    { value: 'Country', label: 'Country' },
    { value: 'Ship type', label: 'Ship Type' },
    { value: 'Direction', label: 'Direction' },
  ],
  // Passageline Ships report
  'Passageline Ships': [
    { value: 'Country', label: 'Country' },
    { value: 'Ship type', label: 'Ship Type' },
    { value: 'Direction', label: 'Direction' },
  ],
  // Track history report
  Track: [
    { value: 'Country', label: 'Country' },
    { value: 'Ship type', label: 'Ship Type' },
  ],
  // Trips report
  Trips: [
    { value: 'Country', label: 'Country' },
    { value: 'Ship type', label: 'Ship Type' },
    { value: 'From port', label: 'From Port' },
    { value: 'To port', label: 'To Port' },
  ],
  // Near miss ship to ship
  'Near Miss Ship to Ship': [
    { value: 'Country', label: 'Country' },
    { value: 'Ship type', label: 'Ship Type' },
  ],
  // Near miss ship to point
  'Near Miss Ship to Point': [
    { value: 'Country', label: 'Country' },
    { value: 'Ship type', label: 'Ship Type' },
  ],
  // Fishing report
  Fishing: [
    { value: 'Country', label: 'Country' },
    { value: 'Ship type', label: 'Ship Type' },
  ],
  // Transponder off
  'Transponder Off': [
    { value: 'Country', label: 'Country' },
    { value: 'Ship type', label: 'Ship Type' },
  ],
  // Watchdog events
  'Watchdog Events': [
    { value: 'Event type', label: 'Event Type' },
    { value: 'Country', label: 'Country' },
    { value: 'Ship type', label: 'Ship Type' },
  ],
  // AtoN reports
  'AtoN Overview': [
    { value: 'Type', label: 'Type' },
    { value: 'Status', label: 'Status' },
  ],
  'AtoN Detailed': [
    { value: 'Type', label: 'Type' },
    { value: 'Status', label: 'Status' },
  ],
  // Multi-shape report
  'Multi-Shape': [
    { value: 'Country', label: 'Country' },
    { value: 'Ship type', label: 'Ship Type' },
    { value: 'Shape', label: 'Shape' },
  ],
  // Density report
  Density: [
    { value: 'Country', label: 'Country' },
    { value: 'Ship type', label: 'Ship Type' },
  ],
  // Coverage report
  Coverage: [
    { value: 'Country', label: 'Country' },
    { value: 'Ship type', label: 'Ship Type' },
  ],
  // Ferry route report
  'Ferry Route': [
    { value: 'Country', label: 'Country' },
    { value: 'Ship type', label: 'Ship Type' },
    { value: 'Route', label: 'Route' },
  ],
  // Pilot operations report
  'Pilot Operations': [
    { value: 'Country', label: 'Country' },
    { value: 'Ship type', label: 'Ship Type' },
    { value: 'Operation', label: 'Operation' },
  ],
  // Tugboat operations report
  'Tugboat Operations': [
    { value: 'Country', label: 'Country' },
    { value: 'Ship type', label: 'Ship Type' },
    { value: 'Operation', label: 'Operation' },
  ],
  // Transit pilotage report
  'Transit Pilotage': [
    { value: 'Country', label: 'Country' },
    { value: 'Ship type', label: 'Ship Type' },
  ],
  // SOG distribution report
  'SOG Distribution': [
    { value: 'Country', label: 'Country' },
    { value: 'Ship type', label: 'Ship Type' },
  ],
  // MMSI change report
  'MMSI Change': [
    { value: 'Country', label: 'Country' },
    { value: 'Ship type', label: 'Ship Type' },
  ],
  // CSV Data Export - typically has vessel data
  'CSV Data Export': [
    { value: 'Country', label: 'Country' },
    { value: 'Ship type', label: 'Ship Type' },
  ],
  // PSS reports
  'PSS Overview': [
    { value: 'Status', label: 'Status' },
    { value: 'Country', label: 'Country' },
  ],
  'PSS Detailed': [
    { value: 'Status', label: 'Status' },
    { value: 'Country', label: 'Country' },
  ],
};

/**
 * Default grouping options when report type is not configured.
 * These will be filtered to only show options that actually exist in the data,
 * so unconfigured report types will still show sensible grouping options.
 */
const DEFAULT_GROUPING_OPTIONS: GroupingOption[] = [
  { value: 'Country', label: 'Country' },
  { value: 'Ship type', label: 'Ship Type' },
  { value: 'Destination', label: 'Destination' },
  { value: 'From port', label: 'From Port' },
  { value: 'To port', label: 'To Port' },
  { value: 'Port', label: 'Port' },
  { value: 'Type', label: 'Type' },
  { value: 'Status', label: 'Status' },
  { value: 'Direction', label: 'Direction' },
  { value: 'Event type', label: 'Event Type' },
  { value: 'Shape', label: 'Shape' },
  { value: 'Route', label: 'Route' },
  { value: 'Operation', label: 'Operation' },
];

/**
 * Get chart grouping options for a specific report type
 */
export const getChartOptionsForReportType = (
  reportType: string | undefined,
): GroupingOption[] => {
  if (!reportType) return DEFAULT_GROUPING_OPTIONS;

  // Try exact match first
  if (REPORT_TYPE_CHART_CONFIG[reportType]) {
    return REPORT_TYPE_CHART_CONFIG[reportType];
  }

  // Try partial match (e.g., "Area Report" matches "Area")
  const matchedKey = Object.keys(REPORT_TYPE_CHART_CONFIG).find((key) =>
    reportType.toLowerCase().includes(key.toLowerCase()),
  );

  if (matchedKey) {
    return REPORT_TYPE_CHART_CONFIG[matchedKey];
  }

  return DEFAULT_GROUPING_OPTIONS;
};

// Colors for the bars - using primary color with varying opacity for consistency
const CHART_COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--primary) / 0.8)',
  'hsl(var(--primary) / 0.6)',
  'hsl(var(--primary) / 0.4)',
  'hsl(var(--primary) / 0.3)',
];

interface PopupReportChartProps {
  shipData: ReportShipData[] | null;
  isLoading?: boolean;
  reportType?: string;
}

interface ChartDataPoint {
  name: string;
  count: number;
  fill: string;
}

export const PopupReportChart: React.FC<PopupReportChartProps> = ({
  shipData,
  isLoading,
  reportType,
}) => {
  const { messages } = useLocalization();
  const [groupBy, setGroupBy] = useState<string>('Country');

  const noChartDataMessage =
    messages.emptyStateNoData?.replace('{entity}', 'chart') ??
    'No chart data available';

  // Get configured options for this report type
  const configuredOptions = useMemo(
    () => getChartOptionsForReportType(reportType),
    [reportType],
  );

  // Determine which grouping options are available based on the data
  const availableOptions = useMemo(() => {
    if (!shipData?.length) return [];

    const dataKeys = Object.keys(shipData[0]);
    return configuredOptions.filter((option) =>
      dataKeys.some((key) => key.toLowerCase() === option.value.toLowerCase()),
    );
  }, [shipData, configuredOptions]);

  // Find the actual column key (case-insensitive match)
  const actualColumnKey = useMemo(() => {
    if (!shipData?.length) return null;

    const dataKeys = Object.keys(shipData[0]);
    return (
      dataKeys.find((key) => key.toLowerCase() === groupBy.toLowerCase()) ||
      null
    );
  }, [shipData, groupBy]);

  // Group data by selected column
  const chartData = useMemo((): ChartDataPoint[] => {
    if (!shipData?.length || !actualColumnKey) return [];

    const grouped = shipData.reduce(
      (acc, row) => {
        let value = row[actualColumnKey];

        // Handle undefined/null values
        if (value === undefined || value === null || value === '') {
          value = 'Unknown';
        }

        // For flag images, extract country name if possible
        if (typeof value === 'string' && value.includes('/flags/')) {
          const match = value.match(/\/flags\/([^/]+)\./);
          if (match) {
            value = match[1].toUpperCase();
          }
        }

        acc[value] = (acc[value] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    // Sort by count descending and take top 15
    return Object.entries(grouped)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 15)
      .map(([name, count], index) => ({
        name: name.length > 20 ? `${name.substring(0, 17)}...` : name,
        count,
        fill: CHART_COLORS[index % CHART_COLORS.length],
      }));
  }, [shipData, actualColumnKey]);

  // Auto-select first available option if current selection is not available
  React.useEffect(() => {
    if (
      availableOptions.length > 0 &&
      !availableOptions.find((o) => o.value === groupBy)
    ) {
      setGroupBy(availableOptions[0].value);
    }
  }, [availableOptions, groupBy]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner />
      </div>
    );
  }

  if (!shipData?.length) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        {noChartDataMessage}
      </div>
    );
  }

  if (availableOptions.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        No groupable columns found in data
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-muted-foreground">
          Distribution by {groupBy}
        </h3>
        <Select value={groupBy} onValueChange={setGroupBy}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Group by..." />
          </SelectTrigger>
          <SelectContent>
            {availableOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex-1 min-h-0">
        <ChartContainer
          config={{
            count: {
              label: 'Count',
              color: 'hsl(var(--chart-1))',
            },
          }}
          className="h-full w-full"
        >
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              horizontal={true}
              vertical={false}
            />
            <XAxis type="number" />
            <YAxis
              type="category"
              dataKey="name"
              width={90}
              tick={{ fontSize: 12 }}
            />
            <Tooltip
              content={<ChartTooltipContent />}
              cursor={{ fill: 'hsl(var(--muted))' }}
            />
            <Bar dataKey="count" radius={[0, 4, 4, 0]}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ChartContainer>
      </div>

      <div className="text-xs text-muted-foreground mt-2">
        Showing top {chartData.length} of {shipData.length} records
      </div>
    </div>
  );
};
